/**
 * TypeScript view of the `mastermind` format (ADR 0055).
 * Source of truth: `shared/schema/mastermind.{definition,submission,solution,progress}.json`.
 * Colours are numbers, 1..colors: the digit is the colour's name on the keyboard and on
 * the peg, so the puzzle never rests on colour alone.
 */

export type Localized = Record<string, string | undefined>;

export const LENGTHS = [3, 4, 5, 6] as const;
export const COLOR_COUNTS = [4, 5, 6, 7, 8] as const;
export const DEFAULT_LENGTH = 4;
export const DEFAULT_COLORS = 6;

export interface MastermindDefinition {
  puzzleType: "mastermind";
  typeVersion: number;
  /** Pegs in the code and in every guess, 3 to 6. */
  length: number;
  /** Colours to choose from, numbered 1..colors, 4 to 8. */
  colors: number;
  /** Whether a colour may appear more than once. */
  repeats: boolean;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** One answered guess. */
export interface Guess {
  code: number[];
  /** Right colour, right place. */
  black: number;
  /** Right colour, wrong place. */
  white: number;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface MastermindProgress {
  version: 1;
  /** The guess being composed: one colour per peg, null for an empty hole. */
  draft: (number | null)[];
  /** Every answered guess, in submission order. The found code has black = length. */
  guesses: Guess[];
}

export interface MastermindSubmission {
  code: number[];
}

export function lengthOf(definition: MastermindDefinition): number {
  return definition.length ?? DEFAULT_LENGTH;
}

export function colorsOf(definition: MastermindDefinition): number {
  return definition.colors ?? DEFAULT_COLORS;
}

export function repeatsOf(definition: MastermindDefinition): boolean {
  return definition.repeats ?? true;
}
